import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { formatXAF, formatDate } from "@/lib/format";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell,
} from "recharts";
import { Plus, Trash2, CreditCard, Smartphone, Landmark, Wallet, FileDown } from "lucide-react";

const METHODS = ["orange_money", "mtn_mobile_money", "virement", "cash"];
const METHOD_COLORS = { orange_money: "#CC5500", mtn_mobile_money: "#DDA15E", virement: "#3A86FF", cash: "#4A7C59" };
const METHOD_ICONS = {
  orange_money: Smartphone, mtn_mobile_money: Smartphone, virement: Landmark, cash: Wallet,
};

export default function Paiements() {
  const { t, lang } = useI18n();
  const [payments, setPayments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(false);
  const [newP, setNewP] = useState({ order_id: "", amount: "", method: "orange_money", reference: "", date: "" });

  async function loadAll() {
    const [p, o] = await Promise.all([api.get("/payments"), api.get("/orders")]);
    setPayments(p.data); setOrders(o.data);
  }
  useEffect(() => { loadAll(); }, []);

  const totalsByMethod = payments.reduce((acc, p) => {
    acc[p.method] = (acc[p.method] || 0) + Number(p.amount || 0); return acc;
  }, {});
  const total = payments.reduce((s, p) => s + Number(p.amount || 0), 0);
  const chartData = METHODS.map((m) => ({ name: t(m), amount: totalsByMethod[m] || 0, key: m }));

  async function addPayment() {
    const o = orders.find((x) => x.id === newP.order_id);
    if (!o) return toast.error("Commande requise");
    await api.post("/payments", {
      ...newP, customer_name: o.customer_name, amount: parseFloat(newP.amount || o.total),
      date: newP.date ? new Date(newP.date).toISOString() : new Date().toISOString(),
      reference: newP.reference || `PAY-${Date.now()}`,
    });
    setOpen(false); setNewP({ order_id: "", amount: "", method: "orange_money", reference: "", date: "" });
    toast.success(t("added")); loadAll();
  }
  async function delPayment(id) {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/payments/${id}`); toast.success(t("deleted")); loadAll();
  }

  return (
    <div className="space-y-6" data-testid="payments-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow mb-2">Module 5</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">{t("payments")}</h1>
          <p className="text-muted-foreground mt-1">{payments.length} paiements · Total {formatXAF(total)}</p>
        </div>
        <div className="flex gap-2">
          <a href={`${process.env.REACT_APP_BACKEND_URL}/api/exports/report.xlsx?type=payments`} target="_blank" rel="noopener noreferrer">
            <Button variant="outline" data-testid="export-payments-btn"><FileDown className="w-4 h-4 mr-2" />{t("export_excel")}</Button>
          </a>
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/reports/financial?auth=${localStorage.getItem("agrinova_token") || ""}`}
            target="_blank" rel="noopener noreferrer"
          >
            <Button variant="outline" data-testid="pdf-report-btn"><FileDown className="w-4 h-4 mr-2" />{t("pdf_report")}</Button>
          </a>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="add-payment-btn"><Plus className="w-4 h-4 mr-2" />{t("payments")}</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("add")} — {t("payments")}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Commande</Label>
                <Select value={newP.order_id} onValueChange={(v) => {
                  const o = orders.find((x) => x.id === v);
                  setNewP({ ...newP, order_id: v, amount: o ? o.total : "" });
                }}>
                  <SelectTrigger data-testid="payment-order"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.customer_name} — {formatXAF(o.total)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("amount")} (FCFA)</Label><Input type="number" value={newP.amount} onChange={(e) => setNewP({ ...newP, amount: e.target.value })} /></div>
              <div>
                <Label>{t("method")}</Label>
                <Select value={newP.method} onValueChange={(v) => setNewP({ ...newP, method: v })}>
                  <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{METHODS.map((m) => <SelectItem key={m} value={m}>{t(m)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>{t("reference")}</Label><Input value={newP.reference} onChange={(e) => setNewP({ ...newP, reference: e.target.value })} /></div>
              <div><Label>{t("date")}</Label><Input type="date" value={newP.date} onChange={(e) => setNewP({ ...newP, date: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={addPayment} data-testid="payment-save">{t("save")}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger">
        {METHODS.map((m) => {
          const Icon = METHOD_ICONS[m];
          return (
            <Card key={m} className="card-hover border-border">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="label-eyebrow">{t(m)}</div>
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${METHOD_COLORS[m]}22`, color: METHOD_COLORS[m] }}>
                    <Icon className="w-4 h-4" />
                  </div>
                </div>
                <div className="text-2xl font-bold mt-3" style={{ color: METHOD_COLORS[m] }}>
                  {formatXAF(totalsByMethod[m] || 0)}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold mb-4">{t("revenue_by_method")}</h3>
          <div className="h-64">
            <ResponsiveContainer>
              <BarChart data={chartData}>
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => formatXAF(v)} />
                <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                  {chartData.map((e, i) => <Cell key={i} fill={METHOD_COLORS[e.key]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left border-b border-border bg-secondary/50">
              <tr>
                <th className="p-3">{t("date")}</th>
                <th>{t("customer")}</th>
                <th>{t("method")}</th>
                <th>{t("reference")}</th>
                <th>{t("amount")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-border/60 last:border-0">
                  <td className="p-3">{formatDate(p.date, lang)}</td>
                  <td className="font-medium">{p.customer_name}</td>
                  <td><Badge variant="secondary">{t(p.method)}</Badge></td>
                  <td className="font-mono text-xs">{p.reference}</td>
                  <td className="font-semibold text-primary">{formatXAF(p.amount)}</td>
                  <td><Button variant="ghost" size="sm" onClick={() => delPayment(p.id)}><Trash2 className="w-4 h-4" /></Button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
